import React, { useEffect, useState, useMemo } from 'react';
import { useIntl } from 'umi';
import { Table, TableAddon, TableColumn } from 'tea-component';
import Abnormal from '@home/components/Abnormal';
import ColumnsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import BudgetChangeLineChart from './BudgetChangeLineChart';
import MilestoneDateCell from './MilestoneDateCell';
import DevBudgetChangePercentage from './Percentage';
// import useRequest from '@gp/hooks/useRequest';
import { fetchApprovedTotalDevBudgets } from '@gp/services/finance';
import { formatDateStr } from '@gp/utils/common';
import { formatMilestoneDateByStatus } from '@gp/utils/milestone';
import { newMathFormatOfDevBudget, formatPercentage } from '../utils';
import type { IBaseProps } from '@home/types/common';
import type { IDevBudgetChangeVO } from '@gp/types/finance';
import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import type { ITabType } from '../types';

interface IApprovedTotalDevBudgetProps extends IBaseProps {
  activeTab: ITabType;
  projectId?: string;
  currentMilestone?: IMilestone | null;
  setIsNoData?: React.Dispatch<React.SetStateAction<boolean>>;
}

const { autotip } = Table.addons;

const NODATA_TEXT = 'Approved Total Development Budget have not been updated at this moment.';

const ApprovedTotalDevBudget: React.FC<IApprovedTotalDevBudgetProps> = (props) => {
  const { style, activeTab, projectId, currentMilestone, setIsNoData } = props;
  const [list, setList] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  const { formatMessage } = useIntl();

  // const { loading, data: res } = useRequest(fetchApprovedTotalDevBudgets, {
  //   defaultParams: [{ project_id: projectId || '' }],
  // });

  const runData = async () => {
    try {
      setLoading(true);
      const res = await fetchApprovedTotalDevBudgets({ project_id: projectId || '' });
      const { list } = res?.data || {};
      setList(list || []);
    } catch (exception) {
      console.error(exception);
    } finally {
      setLoading(false);
    }
  };

  const isListEmpty = useMemo(() => {
    return !Array.isArray(list) || !list.length;
  }, [list]);

  const autotipAddon = autotip({
    emptyText: (
      <div style={{ height: 172 }} className="dbg-flex-center">
        {NODATA_TEXT}
      </div>
    ),
  });
  const addons: TableAddon<IDevBudgetChangeVO>[] = [autotipAddon];
  const columns: TableColumn<IDevBudgetChangeVO>[] = [
    {
      key: 'milestone_id',
      header: (
        <ColumnsCenter width={360} align="left">
          <span>Milestone (Milestone Date)</span>
        </ColumnsCenter>
      ),
      width: '25%',
      align: 'left',
      render: (record) => {
        const {
          milestone_id: milestoneId,
          milestone,
          date_confirm: dateConfirm,
          date,
        } = record || {};
        const isCurrent = currentMilestone?.milestone_id === milestoneId;
        return (
          <ColumnsCenter width={360} align="left">
            <MilestoneDateCell
              isCurrent={isCurrent}
              name={milestone}
              date={date}
              dateFormatter={(payload) => formatMilestoneDateByStatus(payload, dateConfirm)}
            />
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'update_time',
      header: (
        <ColumnsCenter width={134} align="left">
          <span>Update Time</span>
        </ColumnsCenter>
      ),
      width: '24%',
      align: 'center',
      render: (record) => {
        const { update_time: updateTime } = record || {};
        return (
          <ColumnsCenter width={134} align="left">
            <span>{formatDateStr(updateTime) || '-'}</span>
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'budget',
      header: (
        <ColumnsCenter width={150} align="right">
          <span>Total Budget($)</span>
        </ColumnsCenter>
      ),
      width: '24%',
      align: 'center',
      render: (record) => {
        const { budget } = record || {};
        return (
          <ColumnsCenter width={150} align="right">
            <span>{newMathFormatOfDevBudget(budget)}</span>
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'budget_change',
      header: (
        <ColumnsCenter width={294} align="center">
          Percentage Change
          <br />
          (Between Current and Previous MS)
        </ColumnsCenter>
      ),
      width: '27%',
      align: 'center',
      render: (record) => {
        const { budget_change: budgetChange } = record || {};
        return (
          <ColumnsCenter width={294} align="right">
            <DevBudgetChangePercentage value={budgetChange} />
          </ColumnsCenter>
        );
      },
    },
  ];

  const chartMap: Record<ITabType, { content: string; component: React.ReactElement }> = {
    'discount-chart': {
      content: formatMessage({ id: 'dbg_common_line_chart' }),
      component: (
        <Abnormal
          style={{ ...style, height: 340 }}
          loadingMsg="Loading..."
          nodataMsg={NODATA_TEXT}
          isLoading={loading}
          isNoData={isListEmpty}
          isError={false}
          component={
            <BudgetChangeLineChart
              valueFormatter={newMathFormatOfDevBudget as any}
              percentageFormatter={formatPercentage}
              data={list}
            />
          }
        />
      ),
    },
    sheet: {
      content: formatMessage({ id: 'dbg_common_table' }),
      component: (
        <Abnormal
          style={{ ...style, height: 340 }}
          loadingMsg="Loading..."
          nodataMsg={NODATA_TEXT}
          isLoading={loading}
          isNoData={false}
          isError={false}
          component={
            <div className="dbg-project-details__finance__dev-budget-change-card__table-container dbg-budget-change-table-container">
              <Table
                className="dbg-project-details__finance__dev-budget-change-card__bordered-table dbg-budget-change-table"
                bordered
                recordKey="milestone_id"
                addons={addons}
                columns={columns}
                records={list || []}
              />
              {!isListEmpty && (
                <div className="dbg-project-details__finance__dev-budget-change-card__table-footer">
                  {list!.length || 0} total items
                </div>
              )}
            </div>
          }
        />
      ),
    },
  };

  useEffect(() => {
    setIsNoData?.(isListEmpty);
  }, [isListEmpty]);

  useEffect(() => {
    if (projectId) {
      runData();
    }
  }, [projectId]);

  return chartMap[activeTab]?.component;
};

export default ApprovedTotalDevBudget;
